// apps/radar/src/routes/__root.tsx
// Version: 2026.4.25
// ForgeRadar Console root layout with MSAL auth gate. Mirrors apps/web's
// pattern — auth state from useAuth(), redirect to login when not signed in,
// loading shell while MSAL initializes. There are no public routes in
// ForgeRadar Phase 1; every page is gated.

import { createRootRoute, Outlet, Link, useLocation } from '@tanstack/react-router';
import { useEffect } from 'react';
import { useAuth } from '@forgetrack/auth';
import { C, F } from '../components/engine';

const NAV_ITEMS = [
  { id: 'dashboard', label: 'Dashboard', icon: '⚡', path: '/',        accent: C.neon   },
  { id: 'leads',     label: 'Leads',     icon: '🎯', path: '/leads',   accent: C.cyan   },
  { id: 'sources',   label: 'Sources',   icon: '📡', path: '/sources', accent: C.violet },
  { id: 'review',    label: 'Review',    icon: '🔬', path: '/review',  accent: C.amber  },
] as const;

function RootLayout() {
  const location = useLocation();
  const { isInitialized, isLoading, isAuthenticated, login, logout, account } = useAuth();

  // Auto-redirect to login once MSAL has finished initializing and decided
  // the user isn't authenticated. Same pattern as apps/web.
  useEffect(() => {
    if (isInitialized && !isAuthenticated && !isLoading) {
      login();
    }
  }, [isInitialized, isAuthenticated, isLoading, login]);

  // ── Loading shell while MSAL initializes or redirects ──────────────────
  if (!isInitialized || isLoading) {
    return <AuthGate label="Initializing ForgeRadar..." />;
  }

  if (!isAuthenticated) {
    return <AuthGate label="Redirecting to sign in..." />;
  }

  // ── Authenticated layout ───────────────────────────────────────────────
  return (
    <div style={{ display: 'flex', minHeight: '100vh' }}>
      <nav style={{
        width: 240,
        background: C.surface,
        borderRight: `1px solid ${C.faint}`,
        display: 'flex',
        flexDirection: 'column',
        padding: '20px 0',
        flexShrink: 0,
      }}>
        <div style={{
          padding: '8px 20px 24px',
          fontFamily: F.display,
          fontWeight: 700,
          fontSize: 20,
          color: C.cyan,
          letterSpacing: '-0.02em',
        }}>
          📡 ForgeRadar
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 3, padding: '0 10px' }}>
          {NAV_ITEMS.map(item => {
            const active = item.path === '/'
              ? location.pathname === '/'
              : location.pathname.startsWith(item.path);
            const accent = item.accent;
            return (
              <Link
                key={item.id}
                to={item.path}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 12,
                  padding: '11px 12px',
                  borderRadius: 8,
                  fontSize: 15,
                  fontWeight: active ? 600 : 500,
                  color: active ? accent : C.textSoft,
                  background: active ? `${accent}14` : 'transparent',
                  textDecoration: 'none',
                  transition: 'all 0.15s ease',
                  border: active ? `1px solid ${accent}28` : '1px solid transparent',
                  fontFamily: F.display,
                }}
              >
                <span style={{ fontSize: 17 }}>{item.icon}</span>
                {item.label}
              </Link>
            );
          })}
        </div>

        <div style={{ flex: 1 }} />

        {/* Signed-in user + logout */}
        <div style={{
          padding: '8px 16px',
          borderTop: `1px solid ${C.faint}`,
          fontFamily: F.display,
        }}>
          <div style={{ fontSize: 11, color: C.dim, marginBottom: 4 }}>SIGNED IN AS</div>
          <div style={{
            fontSize: 12, color: C.textSoft,
            overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
            marginBottom: 8,
          }}>
            {account?.username ?? account?.name ?? '—'}
          </div>
          <button
            onClick={() => logout()}
            style={{
              width: '100%',
              padding: '6px 8px',
              fontSize: 11,
              fontFamily: F.mono,
              letterSpacing: '0.08em',
              color: C.muted,
              background: 'transparent',
              border: `1px solid ${C.faint}`,
              borderRadius: 6,
              cursor: 'pointer',
            }}
          >
            SIGN OUT
          </button>
        </div>

        <div style={{
          padding: '12px 20px',
          fontFamily: F.mono,
          fontSize: 10,
          letterSpacing: '0.15em',
          color: C.dim,
          textTransform: 'uppercase',
        }}>
          Phase 1 Sprint 1
        </div>
      </nav>

      <main style={{ flex: 1, overflow: 'auto' }}>
        <Outlet />
      </main>
    </div>
  );
}

function AuthGate({ label }: { label: string }) {
  return (
    <div style={{
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      minHeight: '100vh',
      background: C.bg,
      fontFamily: F.display,
      color: C.textSoft,
    }}>
      <div style={{ textAlign: 'center' }}>
        <div style={{
          fontSize: 48,
          color: C.cyan,
          marginBottom: 16,
          textShadow: `0 0 24px ${C.cyan}80`,
          animation: 'engine-icon-pulse 1.6s ease-in-out infinite',
        }}>
          📡
        </div>
        <div style={{
          fontSize: 12,
          fontFamily: F.mono,
          letterSpacing: '0.18em',
          color: C.muted,
          textTransform: 'uppercase',
        }}>
          {label}
        </div>
      </div>
    </div>
  );
}

export const Route = createRootRoute({ component: RootLayout });
